import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

const COLLECTION_NAME = "leoActions";

/**
 * Leo Actions Model
 * Handles all Clumse AI actions and automation data
 *
 * Data Structure:
 * {
 *   id: string,                    // Document ID
 *   ownerId: string,               // Property owner/landlord ID
 *   actionData: {                  // Action details
 *     actionType: string,          // 'message_sent', 'reminder_sent', etc.
 *     action: string,              // Human-readable action description
 *     target: string,              // Target entity (tenant, unit, etc.)
 *     parameters: object           // Action-specific parameters
 *   },
 *   result: {                      // Action result
 *     data: {
 *       status: string,            // 'completed', 'pending', 'failed'
 *       message: string,           // Result message
 *       suggestedAction: string     // Suggested follow-up action
 *     }
 *   },
 *   description: string,           // Human-readable description
 *   entityName: string,           // Name of the entity (tenant name)
 *   propertyAddress: string,       // Property/unit address
 *   priority: string,              // 'low', 'medium', 'high'
 *   createdAt: timestamp,          // When the action was created
 *   updatedAt: timestamp,          // When the action was last updated
 *   completedAt: timestamp         // When the action was completed
 * }
 */

export const leoActionsModel = {
  /**
   * Get all leo actions for the current user
   */
  async getAllActions() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log("[Leo Actions] Getting all actions for user:", user.uid);

      const actionsRef = collection(db, COLLECTION_NAME);
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      console.log(
        "[Leo Actions] Found",
        querySnapshot.docs.length,
        "total actions"
      );

      const actions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log("[Leo Actions] Raw action data:", {
          id: doc.id,
          ownerId: data.ownerId,
          actionData: data.actionData,
          result: data.result,
          description: data.description,
          entityName: data.entityName,
          propertyAddress: data.propertyAddress,
          createdAt: data.createdAt,
          priority: data.priority,
        });
        return {
          id: doc.id,
          ...data,
        };
      });

      return actions;
    } catch (error) {
      console.error("Error getting all leo actions:", error);
      throw error;
    }
  },

  /**
   * Get completed leo actions for statistics
   */
  async getCompletedActions() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log(
        "[Leo Actions] Getting completed actions for user:",
        user.uid
      );

      // First, let's get all actions and filter them in JavaScript
      // This avoids complex Firestore queries that might not match the data structure
      const actionsRef = collection(db, COLLECTION_NAME);
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      console.log(
        "[Leo Actions] Found",
        querySnapshot.docs.length,
        "total actions"
      );

      const allActions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        };
      });

      // Filter for completed actions - handle different possible data structures
      const completedActions = allActions.filter((action) => {
        // Check various possible status locations
        const status =
          action.result?.data?.status ||
          action.result?.status ||
          action.status ||
          "completed"; // Default to completed if no status found

        console.log("[Leo Actions] Action status check:", {
          id: action.id,
          resultDataStatus: action.result?.data?.status,
          resultStatus: action.result?.status,
          actionStatus: action.status,
          finalStatus: status,
        });

        return status === "completed";
      });

      console.log(
        "[Leo Actions] Found",
        completedActions.length,
        "completed actions after filtering"
      );
      return completedActions;
    } catch (error) {
      console.error("Error getting completed leo actions:", error);
      throw error;
    }
  },

  /**
   * Get pending leo actions
   */
  async getPendingActions() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log("[Leo Actions] Getting pending actions for user:", user.uid);

      // Get all actions and filter for pending in JavaScript
      const actionsRef = collection(db, COLLECTION_NAME);
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      console.log(
        "[Leo Actions] Found",
        querySnapshot.docs.length,
        "total actions"
      );

      const allActions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        };
      });

      // Filter for pending actions - handle different possible data structures
      const pendingActions = allActions.filter((action) => {
        // Check various possible status locations
        const status =
          action.result?.data?.status ||
          action.result?.status ||
          action.status ||
          "completed"; // Default to completed if no status found

        console.log("[Leo Actions] Pending status check:", {
          id: action.id,
          resultDataStatus: action.result?.data?.status,
          resultStatus: action.result?.status,
          actionStatus: action.status,
          finalStatus: status,
        });

        return status === "pending";
      });

      console.log(
        "[Leo Actions] Found",
        pendingActions.length,
        "pending actions after filtering"
      );
      return pendingActions;
    } catch (error) {
      console.error("Error getting pending leo actions:", error);
      throw error;
    }
  },

  /**
   * Get recent leo actions (most recent N actions)
   */
  async getRecentActions(limitCount = 10) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log("[Leo Actions] Getting recent actions for user:", user.uid);

      // Get recent actions with limit - don't filter by week, just get the most recent ones
      const actionsRef = collection(db, COLLECTION_NAME);
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      console.log(
        "[Leo Actions] Found",
        querySnapshot.docs.length,
        "recent actions"
      );

      const recentActions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log("[Leo Actions] Action:", {
          id: doc.id,
          actionType: data.actionData?.actionType,
          description: data.description,
          createdAt: data.createdAt,
          status:
            data.result?.data?.status || data.result?.status || data.status,
        });
        return {
          id: doc.id,
          ...data,
        };
      });

      console.log(
        "[Leo Actions] Returning",
        recentActions.length,
        "recent actions"
      );
      return recentActions;
    } catch (error) {
      console.error("Error getting recent leo actions:", error);
      throw error;
    }
  },

  /**
   * Create a new leo action
   */
  async createAction(actionData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      const action = {
        ownerId: user.uid,
        actionData: actionData.actionData || {},
        result: actionData.result || { data: { status: "pending" } },
        description: actionData.description || "Leo action created",
        entityName: actionData.entityName || "Unknown Entity",
        propertyAddress: actionData.propertyAddress || "Unknown Address",
        priority: actionData.priority || "medium",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const actionsRef = collection(db, COLLECTION_NAME);
      const docRef = await addDoc(actionsRef, action);

      console.log("[Leo Actions] Created action:", docRef.id);
      return docRef.id;
    } catch (error) {
      console.error("Error creating leo action:", error);
      throw error;
    }
  },

  /**
   * Update a leo action
   */
  async updateAction(actionId, updateData) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      const actionRef = doc(db, COLLECTION_NAME, actionId);
      const updatePayload = {
        ...updateData,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(actionRef, updatePayload);

      console.log("[Leo Actions] Updated action:", actionId);
      return true;
    } catch (error) {
      console.error("Error updating leo action:", error);
      throw error;
    }
  },

  /**
   * Calculate time saved for each action type (in minutes)
   */
  getTimeSavedPerAction(actionType, description = "") {
    const timeSavings = {
      // Communication actions
      message_sent: 5, // 5 minutes per message
      reminder_sent: 3, // 3 minutes per reminder
      reminder: 3, // Alternative reminder type
      notification_sent: 2, // 2 minutes per notification

      // Maintenance actions
      maintenance_coordinated: 15, // 15 minutes per maintenance coordination
      maintenance_scheduled: 10, // 10 minutes per scheduling
      repair_coordinated: 15, // 15 minutes per repair coordination
      maintenance_followup: 8, // 8 minutes per follow-up

      // Financial actions
      late_fee_applied: 5, // 5 minutes per late fee application
      payment_processed: 3, // 3 minutes per payment processing
      rent_collected: 8, // 8 minutes per rent collection
      deposit_processed: 10, // 10 minutes per deposit processing

      // Leasing workflows
      lease_renewal_sent: 10, // 10 minutes per lease renewal
      lease_renewal: 20, // 20 minutes: reviewing terms, creating new doc, updating records
      lease_mtm_conversion: 15, // 15 minutes: evaluating terms, updating status
      lease_termination: 15, // 15 minutes: reviewing obligations, updating records
      lease_reminder: 5, // 5 minutes per lease reminder

      // Tenant management
      tenant_screened: 45, // 45 minutes per tenant screening
      application_reviewed: 20, // 20 minutes per application review
      tenant_communication: 8, // 8 minutes per tenant communication

      // Reporting
      report_generated: 25, // 25 minutes per report generation
      monthly_report: 25, // 25 minutes per monthly report
      financial_summary: 15, // 15 minutes per financial summary

      // Inspection and compliance
      inspection_scheduled: 10, // 10 minutes per inspection scheduling
      compliance_check: 20, // 20 minutes per compliance check

      // Landlord AI chat (Leo assistant)
      ai_assistance: 5,

      // Default fallback
      default: 5, // 5 minutes for unknown actions
    };

    // Check for specific action types
    if (timeSavings[actionType]) {
      return timeSavings[actionType];
    }

    // Fallback to description-based matching for more flexible detection
    const desc = description.toLowerCase();
    if (desc.includes("message") || desc.includes("sent message")) return 5;
    if (desc.includes("reminder") || desc.includes("rent reminder")) return 3;
    if (desc.includes("maintenance") || desc.includes("repair")) return 15;
    if (desc.includes("late fee") || desc.includes("late fee applied"))
      return 5;
    if (desc.includes("renewed lease") || desc.includes("lease renewal"))
      return 20;
    if (desc.includes("month-to-month") || desc.includes("converted lease"))
      return 15;
    if (desc.includes("terminated lease") || desc.includes("lease termination"))
      return 15;
    if (desc.includes("screening") || desc.includes("tenant screening"))
      return 45;
    if (desc.includes("report") || desc.includes("generated report")) return 25;
    if (desc.includes("payment") || desc.includes("rent collected")) return 8;
    if (desc.includes("notification") || desc.includes("alert")) return 2;
    if (desc.includes("ai provided property management assistance")) return 5;

    return timeSavings.default;
  },

  /**
   * Get action statistics including time saved
   */
  async getActionStats() {
    try {
      const completedActions = await this.getCompletedActions();

      console.log(
        "[Leo Actions] Calculating stats from completed actions:",
        completedActions.length
      );

      // Filter for actions from this week (Monday - Sunday)
      const now = new Date();
      const startOfWeek = new Date(now);
      // Calculate Monday of this week: if today is Sunday (0), go back 6 days, otherwise go back (day - 1) days
      const dayOfWeek = now.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setDate(now.getDate() - daysToSubtract);
      startOfWeek.setHours(0, 0, 0, 0);

      const thisWeekActions = completedActions.filter((action) => {
        if (!action.createdAt) return false;
        const actionDate = action.createdAt.toDate
          ? action.createdAt.toDate()
          : new Date(action.createdAt);
        return actionDate >= startOfWeek;
      });

      console.log("[Leo Actions] Filtering for this week:", {
        total: completedActions.length,
        thisWeek: thisWeekActions.length,
        startOfWeek: startOfWeek,
      });

      // Log each action's actionType for debugging
      thisWeekActions.forEach((action) => {
        console.log("[Leo Actions] Action for stats:", {
          id: action.id,
          actionType: action.actionData?.actionType,
          description: action.description,
          actionData: action.actionData,
        });
      });

      const stats = {
        messagesSent: thisWeekActions.filter((a) => {
          const actionType = a.actionData?.actionType;
          return (
            actionType === "message_sent" ||
            actionType === "message" ||
            a.description?.toLowerCase().includes("message")
          );
        }).length,
        remindersSent: thisWeekActions.filter((a) => {
          const actionType = a.actionData?.actionType;
          return (
            actionType === "reminder_sent" ||
            actionType === "reminder" ||
            a.description?.toLowerCase().includes("reminder")
          );
        }).length,
        maintenanceRequests: thisWeekActions.filter((a) => {
          const actionType = a.actionData?.actionType;
          return (
            actionType === "maintenance_request" ||
            actionType === "maintenance" ||
            a.description?.toLowerCase().includes("maintenance")
          );
        }).length,
        lateFeesApplied: thisWeekActions.filter((a) => {
          const actionType = a.actionData?.actionType;
          return (
            actionType === "late_fee_applied" ||
            actionType === "late_fee" ||
            a.description?.toLowerCase().includes("late fee")
          );
        }).length,
        depositsProcessed: thisWeekActions.filter((a) => {
          const actionType = a.actionData?.actionType;
          return (
            actionType === "deposit_processed" ||
            actionType === "deposit" ||
            a.description?.toLowerCase().includes("deposit")
          );
        }).length,
        screeningCompleted: thisWeekActions.filter((a) => {
          const actionType = a.actionData?.actionType;
          return (
            actionType === "screening_completed" ||
            actionType === "screening" ||
            a.description?.toLowerCase().includes("screening")
          );
        }).length,
        totalActions: thisWeekActions.length,
        totalTimeSaved: 0,
        timeSavedBreakdown: {
          communication: 0,
          maintenance: 0,
          financial: 0,
          leaseManagement: 0,
          tenantManagement: 0,
          reporting: 0,
          other: 0,
        },
      };

      // Calculate time saved for each action (from this week)
      thisWeekActions.forEach((action) => {
        const actionType =
          action.actionData?.actionType || action.actionType || "unknown";
        const description =
          action.description || action.actionData?.description || "";
        const timeSaved = this.getTimeSavedPerAction(actionType, description);

        stats.totalTimeSaved += timeSaved;

        // Categorize time savings
        if (
          actionType === "message_sent" ||
          actionType === "message" ||
          actionType === "reminder_sent" ||
          actionType === "reminder" ||
          description.toLowerCase().includes("message") ||
          description.toLowerCase().includes("reminder")
        ) {
          stats.timeSavedBreakdown.communication += timeSaved;
        } else if (
          actionType.includes("maintenance") ||
          description.toLowerCase().includes("maintenance")
        ) {
          stats.timeSavedBreakdown.maintenance += timeSaved;
        } else if (
          actionType.includes("late_fee") ||
          actionType.includes("payment") ||
          actionType.includes("deposit") ||
          description.toLowerCase().includes("late fee") ||
          description.toLowerCase().includes("payment") ||
          description.toLowerCase().includes("deposit")
        ) {
          stats.timeSavedBreakdown.financial += timeSaved;
        } else if (
          actionType.includes("lease") ||
          description.toLowerCase().includes("lease")
        ) {
          stats.timeSavedBreakdown.leaseManagement += timeSaved;
        } else if (
          actionType.includes("screening") ||
          actionType.includes("tenant") ||
          description.toLowerCase().includes("screening") ||
          description.toLowerCase().includes("tenant")
        ) {
          stats.timeSavedBreakdown.tenantManagement += timeSaved;
        } else if (
          actionType.includes("report") ||
          description.toLowerCase().includes("report")
        ) {
          stats.timeSavedBreakdown.reporting += timeSaved;
        } else {
          stats.timeSavedBreakdown.other += timeSaved;
        }
      });

      console.log("[Leo Actions] Calculated stats:", stats);
      return stats;
    } catch (error) {
      console.error("Error getting action stats:", error);
      throw error;
    }
  },

  /**
   * Format time ago for display
   */
  formatTimeAgo(timestamp) {
    if (!timestamp) return "Unknown time";

    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? "s" : ""} ago`;
    }
  },

  /**
   * Get icon for action type
   */
  getActionIcon(type) {
    const icons = {
      message_sent: "MessageOutlined",
      reminder_sent: "ClockCircleOutlined",
      maintenance_request: "ToolOutlined",
      late_fee_applied: "DollarOutlined",
      deposit_processed: "BankOutlined",
      screening_completed: "SafetyOutlined",
      lease_renewal: "FileTextOutlined",
      eviction: "ExclamationCircleOutlined",
      // Legacy types for backward compatibility
      message: "MessageOutlined",
      reminder: "ClockCircleOutlined",
      maintenance: "ToolOutlined",
      late_fee: "DollarOutlined",
      deposit: "BankOutlined",
      screening: "SafetyOutlined",
    };
    return icons[type] || "InfoCircleOutlined";
  },
};

export default leoActionsModel;
